/**
 * Client-side filter drawer for the Smart Match / Sourcing canvas.
 *
 * Layout (post user-feedback, 21-Apr — Wrangle parity, ref: image 1):
 *   - Right-side slide-in drawer (not a toolbar popover) so we can afford
 *     per-criterion sliders and still feel at home alongside the result
 *     stack.
 *   - One slider per dimension (Overall, Skills, Experience, Location,
 *     Semantic similarity) with a compact right-aligned percentage.
 *   - Classic skills-include / skills-exclude / location / email /
 *     linkedin secondary filters below the sliders.
 *   - Sticky footer with a prominent "Clear filters" button.
 *
 * Operates entirely on the already-loaded result set (no round-trips) —
 * the agent composer is NOT called from here.
 */

import { useEffect, useRef } from 'react';
import { RotateCcw, X } from 'lucide-react';

/**
 * Score bands (docs/smart-match §13.3). `bandOf` in `lib/matchDisplay`
 * buckets `composite_score` into one of these three; the filter panel
 * exposes them as toggle pills so recruiters can quickly scope to e.g.
 * "only Strong matches" without dragging the overall slider.
 */
export type MatchBand = 'high' | 'mid' | 'low';

export interface CanvasFilters {
  minScore: number;
  minSkillOverlap: number;
  minExperienceAlignment: number;
  minLocationFit: number;
  minSemanticSimilarity: number;
  skillsInclude: string;
  skillsExclude: string;
  locationContains: string;
  requireEmail: boolean;
  requireLinkedin: boolean;
  /**
   * 21-Apr audit gap: band pills (Strong / Possible / Weak). Empty
   * array = no band filter. Kept as an array so the order on disk
   * matches the pill order in the panel.
   */
  bands: readonly MatchBand[];
  /** Multi-select by client id. Empty array = all clients. */
  clientIds: readonly string[];
  /** Multi-select by job (JD) id. Empty array = all JDs. */
  jobIds: readonly string[];
  /**
   * Free-text search across candidate name, current title, current
   * company and location. Applied as a case-insensitive substring
   * match — no fuzzy logic (kept cheap since this runs on every
   * result for every keystroke).
   */
  search: string;
  /**
   * Multi-select by `candidate.source_channel`. Empty array = all channels.
   * Values are the raw `source_channel` strings from the result set so no
   * normalisation is required at filter time.
   */
  sourceChannels: readonly string[];
}

export const DEFAULT_CANVAS_FILTERS: CanvasFilters = {
  minScore: 0,
  minSkillOverlap: 0,
  minExperienceAlignment: 0,
  minLocationFit: 0,
  minSemanticSimilarity: 0,
  skillsInclude: '',
  skillsExclude: '',
  locationContains: '',
  requireEmail: false,
  requireLinkedin: false,
  bands: [],
  clientIds: [],
  jobIds: [],
  search: '',
  sourceChannels: [],
};

export function countActiveFilters(f: CanvasFilters): number {
  let n = 0;
  if (f.minScore > 0) n += 1;
  if (f.minSkillOverlap > 0) n += 1;
  if (f.minExperienceAlignment > 0) n += 1;
  if (f.minLocationFit > 0) n += 1;
  if (f.minSemanticSimilarity > 0) n += 1;
  if (f.skillsInclude.trim()) n += 1;
  if (f.skillsExclude.trim()) n += 1;
  if (f.locationContains.trim()) n += 1;
  if (f.requireEmail) n += 1;
  if (f.requireLinkedin) n += 1;
  if (f.bands.length > 0) n += 1;
  if (f.clientIds.length > 0) n += 1;
  if (f.jobIds.length > 0) n += 1;
  if (f.search.trim()) n += 1;
  if (f.sourceChannels.length > 0) n += 1;
  return n;
}

/**
 * Option row for a single client / job in the multi-select lists.
 * The parent computes these from the current result set so the panel
 * only offers values that would actually match something — avoids
 * showing "Acme Corp (0)" for a client that isn't present.
 */
export interface CanvasFilterOption {
  id: string;
  label: string;
  /** Optional count — rendered in muted text next to the label. */
  count?: number;
}

interface CanvasFilterPanelProps {
  open: boolean;
  value: CanvasFilters;
  onChange: (next: CanvasFilters) => void;
  onClose: () => void;
  onReset: () => void;
  /** Clients present in the current result set. Empty = hide the row. */
  clientOptions?: readonly CanvasFilterOption[];
  /** JDs present in the current result set. Empty = hide the row. */
  jobOptions?: readonly CanvasFilterOption[];
  /** Source channels present in the current result set. Empty = hide the row. */
  channelOptions?: readonly CanvasFilterOption[];
}

const BAND_PILLS: Array<{ id: MatchBand; label: string; hint: string }> = [
  { id: 'high', label: 'Strong', hint: '≥ 75 composite score' },
  { id: 'mid', label: 'Possible', hint: '50 – 74 composite score' },
  { id: 'low', label: 'Weak', hint: '< 50 composite score' },
];

const CRITERION_SLIDERS: Array<{
  key: keyof Pick<
    CanvasFilters,
    | 'minScore'
    | 'minSkillOverlap'
    | 'minExperienceAlignment'
    | 'minLocationFit'
    | 'minSemanticSimilarity'
  >;
  label: string;
  hint?: string;
}> = [
  {
    key: 'minScore',
    label: 'Overall candidate profile match',
    hint: 'Composite score across all factors',
  },
  {
    key: 'minSkillOverlap',
    label: 'Skills match',
    hint: 'Hard skill overlap with the brief',
  },
  {
    key: 'minExperienceAlignment',
    label: 'Experience alignment',
    hint: 'Seniority & role history fit',
  },
  {
    key: 'minLocationFit',
    label: 'Location fit',
    hint: 'Preferred geo / timezone',
  },
  {
    key: 'minSemanticSimilarity',
    label: 'Profile semantic similarity',
    hint: 'How close the narrative is to the JD',
  },
];

export function CanvasFilterPanel({
  open,
  value,
  onChange,
  onClose,
  onReset,
  clientOptions = [],
  jobOptions = [],
  channelOptions = [],
}: CanvasFilterPanelProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const patch = (delta: Partial<CanvasFilters>) =>
    onChange({ ...value, ...delta });
  const active = countActiveFilters(value);

  return (
    <>
      {/* Dimmer — click to dismiss, matches Wrangle's right-drawer pattern. */}
      <div
        className="fixed inset-0 z-[150] bg-black/20 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
      />
      <aside
        ref={rootRef}
        role="dialog"
        aria-modal="true"
        aria-label="Filter candidates"
        className="fixed right-0 top-0 z-[160] flex h-screen w-[380px] max-w-[90vw] flex-col border-l shadow-xl"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          background: 'var(--vscode-bg-elevated)',
        }}
      >
        <header
          className="flex h-12 shrink-0 items-center justify-between border-b px-4"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <div className="flex items-center gap-2">
            <span
              className="text-[13px] font-semibold"
              style={{ color: 'var(--vscode-text-primary)' }}
            >
              Filter candidates
            </span>
            {active > 0 ? (
              <span
                className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold tabular-nums"
                style={{
                  background: 'rgba(6,95,70,0.12)',
                  color: '#065F46',
                }}
              >
                {active}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-[6px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
            style={{ color: 'var(--vscode-text-tertiary)' }}
            aria-label="Close filter panel"
            title="Close"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </header>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto ggj-scroll-thin">
          {/*
           * 21-Apr audit close-out — the 3 filter dimensions called out
           * in `docs/smart-match/05_UI_DESIGN §14.6`:
           *   1. Free-text search (name / title / company / location).
           *   2. Band pills (Strong / Possible / Weak).
           *   3. Client + JD multiselect.
           * Rendered before the slider stack so they're the first thing
           * recruiters see — they're the filters most people reach for.
           */}
          <section className="flex flex-col gap-3 px-4 pt-4">
            <Field label="Search">
              <TextInput
                value={value.search}
                onChange={(v) => patch({ search: v })}
                placeholder="Name, title, company, location…"
              />
            </Field>

            <div className="flex flex-col gap-1.5">
              <div
                className="text-[11px] font-semibold uppercase tracking-wider"
                style={{ color: 'var(--vscode-text-tertiary)' }}
              >
                Match band
              </div>
              <div className="flex flex-wrap gap-1.5">
                {BAND_PILLS.map((b) => {
                  const active = value.bands.includes(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => {
                        const next = active
                          ? value.bands.filter((x) => x !== b.id)
                          : [...value.bands, b.id];
                        patch({ bands: next });
                      }}
                      title={b.hint}
                      className="inline-flex h-7 items-center rounded-full border px-2.5 text-[11px] font-medium transition-colors"
                      style={{
                        background: active
                          ? 'rgba(6,95,70,0.12)'
                          : 'var(--vscode-bg-primary)',
                        borderColor: active
                          ? 'rgba(6,95,70,0.4)'
                          : 'var(--vscode-border-primary)',
                        color: active ? '#065F46' : 'var(--vscode-text-secondary)',
                      }}
                      aria-pressed={active}
                    >
                      {b.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {clientOptions.length > 0 ? (
              <MultiSelectList
                label="Client"
                options={clientOptions}
                selectedIds={value.clientIds}
                onChange={(next) => patch({ clientIds: next })}
                emptyHint="No clients in this result set"
              />
            ) : null}

            {jobOptions.length > 0 ? (
              <MultiSelectList
                label="Job description"
                options={jobOptions}
                selectedIds={value.jobIds}
                onChange={(next) => patch({ jobIds: next })}
                emptyHint="No JDs in this result set"
              />
            ) : null}

            {channelOptions.length > 0 ? (
              <MultiSelectList
                label="Source channel"
                options={channelOptions}
                selectedIds={value.sourceChannels}
                onChange={(next) => patch({ sourceChannels: next })}
                emptyHint="No source channels in this result set"
              />
            ) : null}
          </section>

          <div
            className="mx-4 my-3 h-px"
            style={{ background: 'var(--vscode-border-primary)' }}
          />

          <section className="flex flex-col gap-4 px-4 py-4">
            {CRITERION_SLIDERS.map((c) => (
              <CriterionSlider
                key={c.key}
                label={c.label}
                hint={c.hint}
                value={value[c.key]}
                onChange={(v) => patch({ [c.key]: v } as Partial<CanvasFilters>)}
              />
            ))}
          </section>

          <div
            className="mx-4 my-2 h-px"
            style={{ background: 'var(--vscode-border-primary)' }}
          />

          <section className="flex flex-col gap-3 px-4 pb-4 pt-2">
            <h4
              className="text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              Attributes
            </h4>
            <Field label="Skills include" hint="comma-separated (any match)">
              <TextInput
                value={value.skillsInclude}
                onChange={(v) => patch({ skillsInclude: v })}
                placeholder="e.g. Python, Go"
              />
            </Field>
            <Field label="Skills exclude" hint="comma-separated (none match)">
              <TextInput
                value={value.skillsExclude}
                onChange={(v) => patch({ skillsExclude: v })}
                placeholder="e.g. PHP, Ruby"
              />
            </Field>
            <Field label="Location contains">
              <TextInput
                value={value.locationContains}
                onChange={(v) => patch({ locationContains: v })}
                placeholder="e.g. Bengaluru"
              />
            </Field>
            <div className="flex items-center gap-4 pt-1">
              <Toggle
                label="Has email"
                checked={value.requireEmail}
                onChange={(v) => patch({ requireEmail: v })}
              />
              <Toggle
                label="Has LinkedIn"
                checked={value.requireLinkedin}
                onChange={(v) => patch({ requireLinkedin: v })}
              />
            </div>
          </section>
        </div>

        {/* Sticky footer */}
        <footer
          className="flex shrink-0 items-center justify-between gap-2 border-t px-4 py-3"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <button
            type="button"
            onClick={onReset}
            disabled={active === 0}
            className="inline-flex h-8 items-center gap-1.5 rounded-[8px] px-2.5 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)] disabled:opacity-40"
            style={{ color: 'var(--vscode-text-secondary)' }}
            title="Reset all filters"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            Clear filters
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 items-center rounded-[8px] px-3 text-[12px] font-semibold"
            style={{
              background: 'var(--ggj-accent,#10B981)',
              color: '#fff',
            }}
          >
            Done
          </button>
        </footer>
      </aside>
    </>
  );
}

function CriterionSlider({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  onChange: (v: number) => void;
}) {
  const active = value > 0;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div
            className="text-[12px] font-medium leading-tight"
            style={{ color: 'var(--vscode-text-primary)' }}
          >
            {label}
          </div>
          {hint ? (
            <div
              className="mt-0.5 truncate text-[11px]"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              {hint}
            </div>
          ) : null}
        </div>
        <span
          className="shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums"
          style={{
            background: active
              ? 'rgba(6,95,70,0.12)'
              : 'var(--vscode-bg-primary)',
            color: active ? '#065F46' : 'var(--vscode-text-tertiary)',
            border: '1px solid var(--vscode-border-primary)',
          }}
          aria-label={`Minimum ${label} is ${value}%`}
        >
          {value}%
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--ggj-accent,#10B981)]"
        aria-label={label}
      />
    </div>
  );
}

/**
 * Compact multi-select chip list — used for both Client and JD rows.
 * Shows a "×N selected · Clear" affordance above the chips so users can
 * quickly undo their selection without scanning the entire list.
 */
function MultiSelectList({
  label,
  options,
  selectedIds,
  onChange,
  emptyHint,
}: {
  label: string;
  options: readonly CanvasFilterOption[];
  selectedIds: readonly string[];
  onChange: (next: readonly string[]) => void;
  emptyHint: string;
}) {
  const selected = new Set(selectedIds);
  const hasAny = selected.size > 0;
  // Cap to 12 visible options to keep the drawer height sane — the
  // existing Skills Include / Exclude text fields handle power-user
  // long-tail filtering.
  const shown = options.slice(0, 12);
  const hiddenCount = options.length - shown.length;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <div
          className="text-[11px] font-semibold uppercase tracking-wider"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          {label}
        </div>
        {hasAny ? (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-[10px] font-medium transition-colors hover:underline"
            style={{ color: 'var(--vscode-text-tertiary)' }}
            title={`Clear ${label.toLowerCase()} selection`}
          >
            {selected.size} selected · Clear
          </button>
        ) : null}
      </div>
      {options.length === 0 ? (
        <div
          className="text-[11px] italic"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          {emptyHint}
        </div>
      ) : (
        <div className="flex flex-wrap gap-1">
          {shown.map((opt) => {
            const active = selected.has(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => {
                  const next = active
                    ? selectedIds.filter((x) => x !== opt.id)
                    : [...selectedIds, opt.id];
                  onChange(next);
                }}
                aria-pressed={active}
                className="inline-flex h-6 items-center gap-1 rounded-full border px-2 text-[11px] transition-colors"
                style={{
                  background: active
                    ? 'rgba(6,95,70,0.12)'
                    : 'var(--vscode-bg-primary)',
                  borderColor: active
                    ? 'rgba(6,95,70,0.4)'
                    : 'var(--vscode-border-primary)',
                  color: active ? '#065F46' : 'var(--vscode-text-secondary)',
                }}
                title={opt.label}
              >
                <span className="max-w-[180px] truncate">{opt.label}</span>
                {typeof opt.count === 'number' ? (
                  <span
                    className="tabular-nums opacity-60"
                    aria-hidden
                  >
                    {opt.count}
                  </span>
                ) : null}
              </button>
            );
          })}
          {hiddenCount > 0 ? (
            <span
              className="inline-flex h-6 items-center px-1.5 text-[11px] italic"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              +{hiddenCount} more
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span
        className="text-[11px] font-medium"
        style={{ color: 'var(--vscode-text-secondary)' }}
      >
        {label}
        {hint ? (
          <span
            className="ml-1 font-normal"
            style={{ color: 'var(--vscode-text-tertiary)' }}
          >
            · {hint}
          </span>
        ) : null}
      </span>
      {children}
    </label>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-8 rounded-[8px] border px-2 text-[12px] outline-none transition-colors focus:border-[var(--ggj-accent,#10B981)]"
      style={{
        borderColor: 'var(--vscode-border-primary)',
        background: 'var(--vscode-bg-primary)',
        color: 'var(--vscode-text-primary)',
      }}
    />
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12px]">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 accent-[var(--ggj-accent,#10B981)]"
      />
      <span style={{ color: 'var(--vscode-text-primary)' }}>{label}</span>
    </label>
  );
}
